// Library (mockup 06): search, filter chips, shared collections, the puzzle list with a sort.
// Filters live in the URL, so back and forward and a shared link keep them.
import {
  SCORE_BANDS,
  SORTS,
  SORT_NAMES,
  parseFilters,
  type CollectionDto,
  type LibraryFilters,
  type LibraryItem,
  type LibrarySort,
  type ScoreBand,
  type StatusFilter,
} from '@zinc/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { ChipMenu, ToggleChip, chipClass, type MenuOption } from '../components/library/ChipMenu';
import { CollectionDialog } from '../components/library/CollectionDialog';
import { PuzzleRow } from '../components/library/PuzzleRow';
import { SearchIcon } from '../components/icons';
import {
  filterQuery,
  sourceName,
  typeName,
  useCollections,
  useFacets,
  useFavourite,
  useLibrary,
} from '../lib/library';
import { useMe } from '../lib/queries';

const BAND_NAMES: Record<ScoreBand, string> = {
  '0-2': '0–2',
  '2-4': '2–4',
  '4-6': '4–6',
  '6-8': '6–8',
  '8-10': '8–10',
  unrated: 'Unrated',
};

/** "Score 2–6" for neighbouring bands, "Score · 3 bands" otherwise. */
function scoreLabel(bands: readonly ScoreBand[]): string {
  const rated = SCORE_BANDS.filter((b) => b !== 'unrated' && bands.includes(b));
  const unrated = bands.includes('unrated');
  if (rated.length === 0) return 'Unrated';
  const idx = rated.map((b) => SCORE_BANDS.indexOf(b));
  const contiguous = idx.every((v, i) => i === 0 || v === idx[i - 1]! + 1);
  const range = contiguous
    ? `${rated[0]!.split('-')[0]}–${rated[rated.length - 1]!.split('-')[1]}`
    : `${rated.length} bands`;
  return `Score ${range}${unrated ? ' + unrated' : ''}`;
}

function sizeName(size: string): string {
  return size.replace('x', '×');
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function CollectionsStrip(props: {
  collections: CollectionDto[];
  active: string | undefined;
  onPick: (id: string | undefined) => void;
  onNew: () => void;
}) {
  return (
    <section className="flex flex-col gap-2.5">
      <div className="flex items-center justify-between">
        <h2 className="m-0 text-[17px] font-bold">Collections</h2>
        <button
          type="button"
          onClick={props.onNew}
          className="h-9 border-0 bg-transparent px-0 text-[14px] font-semibold text-accent hover:text-accent-dark"
        >
          New collection
        </button>
      </div>
      {props.collections.length === 0 ? (
        <p className="m-0 text-[13px] text-muted">
          Shared lists of puzzles, for both of you. Add puzzles from their details.
        </p>
      ) : (
        <ul className="-mr-4 m-0 flex list-none gap-2.5 overflow-x-auto p-0 pr-4">
          {props.collections.map((c) => {
            const on = props.active === c.id;
            return (
              <li key={c.id} className="shrink-0">
                <button
                  type="button"
                  aria-pressed={on}
                  onClick={() => props.onPick(on ? undefined : c.id)}
                  className={`box-border flex h-full w-[140px] flex-col items-start gap-1 rounded-card border bg-surface p-3 text-left text-ink ${on ? 'border-accent' : 'border-line'}`}
                >
                  <span
                    aria-hidden="true"
                    className="h-1.5 w-8 rounded-[3px]"
                    style={{ background: c.colour }}
                  />
                  <span className="line-clamp-2 text-[14px] font-semibold">{c.name}</span>
                  <span className="text-[12px] text-muted">
                    {c.count} {c.count === 1 ? 'puzzle' : 'puzzles'}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export function LibraryPage() {
  const me = useMe();
  const otherName = me.data?.other?.displayName ?? null;
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => parseFilters(params), [params]);
  const setFilters = (patch: Partial<LibraryFilters>) => {
    const next: LibraryFilters = { ...filters, ...patch };
    for (const k of Object.keys(patch) as (keyof LibraryFilters)[]) {
      const v = patch[k];
      if (v === undefined || (Array.isArray(v) && v.length === 0)) delete next[k];
    }
    setParams(filterQuery(next), { replace: true });
  };

  // Search: typed text goes into the URL a moment after typing stops.
  const [text, setText] = useState(filters.q ?? '');
  const typed = useDebounced(text, 300);
  useEffect(() => {
    if ((typed.trim() || undefined) !== filters.q) setFilters({ q: typed.trim() || undefined });
    // Only when the typed text settles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typed]);
  const search = useRef<HTMLInputElement>(null);
  // Home's search button lands here with ?search=1: focus the box.
  useEffect(() => {
    if (params.get('search') === '1') {
      search.current?.focus();
      const rest = new URLSearchParams(params);
      rest.delete('search');
      setParams(rest, { replace: true });
    }
  }, [params, setParams]);

  const facets = useFacets();
  const collections = useCollections();
  const library = useLibrary(filters);
  const favourite = useFavourite();
  const [dialog, setDialog] = useState<'new' | CollectionDto | null>(null);

  const puzzles = useMemo(
    () => library.data?.pages.flatMap((p) => p.puzzles) ?? [],
    [library.data],
  );
  const total = library.data?.pages[0]?.total ?? null;
  const collectionList = collections.data?.collections ?? [];
  const activeCollection = collectionList.find((c) => c.id === filters.collection);
  const filtered = Object.keys(filters).some((k) => k !== 'sort');

  // Load the next page as the end of the list comes into view.
  const sentinel = useRef<HTMLDivElement>(null);
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = library;
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !hasNextPage || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !isFetchingNextPage) void fetchNextPage();
      },
      { rootMargin: '400px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const onFavourite = (p: LibraryItem) => favourite.mutate({ id: p.id, favourite: !p.favourite });

  // ── Filter menus ─────────────────────────────────────────────────────────
  const f = facets.data;
  const statusOptions: MenuOption[] = [
    { value: 'new', label: 'New' },
    { value: 'not-started', label: 'Not started' },
    { value: 'in-progress', label: 'In progress' },
    { value: 'solved', label: 'Solved by you' },
    ...(otherName ? [{ value: 'other-solved', label: `Solved by ${otherName}` }] : []),
  ];
  const typeOptions: MenuOption[] = [
    ...(f?.kinds ?? []).map((k, i) => ({
      value: k.kind,
      label: typeName(k.kind),
      note: String(k.count),
      group: i === 0 ? 'Grid' : undefined,
    })),
    ...(f?.types ?? []).map((t, i) => ({
      value: t.type,
      label: typeName(t.type, true),
      note: String(t.count),
      group: i === 0 ? 'Rules' : undefined,
    })),
  ];
  const sizeOptions: MenuOption[] = (f?.sizes ?? []).map((s) => ({
    value: s.size,
    label: sizeName(s.size),
    note: String(s.count),
  }));
  const sourceOptions: MenuOption[] = [
    ...(f?.sources ?? []).map((s, i) => ({
      value: s.key,
      label: s.name,
      note: String(s.count),
      group: i === 0 ? 'Source' : undefined,
    })),
    ...(f?.origins ?? []).map((o, i) => ({
      value: `origin:${o.origin}`,
      label: o.origin === 'hand-set' ? 'Hand-set' : 'Generated',
      note: String(o.count),
      group: i === 0 ? 'Made' : undefined,
    })),
  ];
  const sourceSelected = [
    ...(filters.source ?? []),
    ...(filters.origin ? [`origin:${filters.origin}`] : []),
  ];
  const typeSelected = filters.type ?? [];
  const typeLabel =
    typeSelected.length === 1
      ? typeName(typeSelected[0]!)
      : `${typeName(typeSelected[0] ?? '')} +${typeSelected.length - 1}`;
  const sourceLabel = (() => {
    const names = [
      ...(filters.source ?? []).map((k) =>
        sourceName(k, f?.sources.find((s) => s.key === k)?.name),
      ),
      ...(filters.origin ? [filters.origin === 'hand-set' ? 'Hand-set' : 'Generated'] : []),
    ];
    return names.length > 2 ? `${names[0]} +${names.length - 1}` : names.join(', ');
  })();

  return (
    <>
      <header className="mx-auto box-border flex w-full max-w-[720px] flex-col gap-3 px-4 pt-4 pb-2">
        <h1 className="m-0 text-[26px] font-bold tracking-[-0.02em]">Library</h1>
        <label className="flex h-11 items-center gap-2 rounded-[12px] border border-line bg-surface px-3 focus-within:border-accent">
          <SearchIcon size={18} className="shrink-0 text-muted" />
          <input
            ref={search}
            type="search"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Search puzzles, setters, rules"
            aria-label="Search puzzles"
            className="min-w-0 flex-1 border-0 bg-transparent text-[15px] text-ink outline-none"
          />
        </label>
      </header>

      <div className="mx-auto w-full max-w-[720px]">
        <div
          className="flex gap-2 overflow-x-auto px-4 pt-1 pb-3 [scrollbar-width:none]"
          aria-label="Filters"
          role="group"
        >
          <ChipMenu
            name="Score"
            label="Score 0–10"
            activeLabel={filters.score ? scoreLabel(filters.score) : undefined}
            options={SCORE_BANDS.map((b) => ({ value: b, label: BAND_NAMES[b] }))}
            selected={filters.score ?? []}
            multiple
            onChange={(v) => setFilters({ score: v as ScoreBand[] })}
          />
          <ChipMenu
            name="Status"
            label="Status"
            activeLabel={statusOptions.find((o) => o.value === filters.status)?.label}
            options={statusOptions}
            selected={filters.status ? [filters.status] : []}
            onChange={(v) => setFilters({ status: v[0] as StatusFilter | undefined })}
          />
          <ChipMenu
            name="Type"
            label="Type"
            activeLabel={typeSelected.length ? typeLabel : undefined}
            options={typeOptions}
            selected={typeSelected}
            multiple
            onChange={(v) => setFilters({ type: v })}
          />
          {sizeOptions.length > 1 && (
            <ChipMenu
              name="Size"
              label="Size"
              activeLabel={filters.size ? sizeName(filters.size) : undefined}
              options={sizeOptions}
              selected={filters.size ? [filters.size] : []}
              onChange={(v) => setFilters({ size: v[0] })}
            />
          )}
          <ChipMenu
            name="Source"
            label="Source"
            activeLabel={sourceLabel || undefined}
            options={sourceOptions}
            selected={sourceSelected}
            multiple
            onChange={(v) => {
              const origins = v.filter((s) => s.startsWith('origin:'));
              // Hand-set and generated are one choice: the newest tick wins.
              const origin = origins[origins.length - 1]?.slice(7) as
                'hand-set' | 'generated' | undefined;
              setFilters({ source: v.filter((s) => !s.startsWith('origin:')), origin });
            }}
          />
          {otherName ? (
            <ChipMenu
              name="Favourites"
              label="Favourites"
              activeLabel={filters.fav === 'other' ? `${otherName}'s favourites` : 'Favourites'}
              options={[
                { value: 'me', label: 'Yours' },
                { value: 'other', label: `${otherName}'s` },
              ]}
              selected={filters.fav ? [filters.fav] : []}
              onChange={(v) => setFilters({ fav: v[0] as 'me' | 'other' | undefined })}
            />
          ) : (
            <ToggleChip
              label="Favourites"
              active={filters.fav === 'me'}
              onToggle={() => setFilters({ fav: filters.fav ? undefined : 'me' })}
            />
          )}
          {(f?.needsPlugin ?? 0) > 0 && (
            <ToggleChip
              label={`Needs plugin · ${f!.needsPlugin}`}
              active={filters.plugin === true}
              onToggle={() => setFilters({ plugin: filters.plugin ? undefined : true })}
            />
          )}
          {filtered && (
            <button
              type="button"
              onClick={() => {
                setText('');
                setParams(filterQuery({ sort: filters.sort }), { replace: true });
              }}
              className={`${chipClass(false)} text-accent`}
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      <div className="mx-auto box-border flex w-full max-w-[720px] flex-col gap-4 px-4 pb-6">
        <CollectionsStrip
          collections={collectionList}
          active={filters.collection}
          onPick={(id) => setFilters({ collection: id })}
          onNew={() => setDialog('new')}
        />

        <section className="flex flex-col gap-2.5">
          <div className="flex items-center justify-between gap-2">
            <h2 className="m-0 flex min-w-0 items-baseline gap-1.5 text-[17px] font-bold">
              <span className="truncate">
                {activeCollection ? activeCollection.name : filtered ? 'Puzzles' : 'All puzzles'}
              </span>
              {total !== null && (
                <span className="text-[13px] font-medium text-muted" data-testid="library-total">
                  {total.toLocaleString('en-GB')}
                </span>
              )}
            </h2>
            <div className="flex items-center">
              {activeCollection && (
                <button
                  type="button"
                  onClick={() => setDialog(activeCollection)}
                  className="h-8 border-0 bg-transparent px-2.5 text-[13px] font-semibold text-accent"
                >
                  Edit
                </button>
              )}
              <ChipMenu
                name="Sort"
                label={SORT_NAMES[filters.sort ?? 'newest']}
                plain
                required
                align="right"
                options={SORTS.map((s) => ({ value: s, label: SORT_NAMES[s] }))}
                selected={[filters.sort ?? 'newest']}
                onChange={(v) => setFilters({ sort: v[0] as LibrarySort })}
              />
            </div>
          </div>

          {library.isPending ? (
            <div className="h-40 rounded-card border border-line bg-surface" />
          ) : library.isError ? (
            <p role="alert" className="m-0 text-[14px] text-mistake">
              The library didn’t load. Check your connection and try again.
            </p>
          ) : puzzles.length === 0 ? (
            <div className="rounded-card border border-line bg-surface px-4 py-5 text-[14px] text-muted">
              {filtered
                ? 'No puzzles match. Try fewer filters.'
                : 'No puzzles yet. New ones arrive from the sources every morning.'}
            </div>
          ) : (
            <ul
              aria-label="Puzzles"
              className="m-0 flex list-none flex-col rounded-card border border-line bg-surface p-0"
            >
              {puzzles.map((p, i) => (
                <PuzzleRow
                  key={p.id}
                  p={p}
                  otherName={otherName}
                  last={i === puzzles.length - 1}
                  onFavourite={onFavourite}
                />
              ))}
            </ul>
          )}
          <div ref={sentinel} />
          {hasNextPage && (
            <button
              type="button"
              disabled={isFetchingNextPage}
              onClick={() => void fetchNextPage()}
              className="h-11 rounded-btn border border-line bg-surface text-[14px] font-semibold text-ink disabled:opacity-60"
            >
              {isFetchingNextPage ? 'Loading…' : 'Show more'}
            </button>
          )}
        </section>
      </div>

      {dialog && (
        <CollectionDialog
          collection={dialog === 'new' ? undefined : dialog}
          onClose={() => setDialog(null)}
          onDeleted={() => setFilters({ collection: undefined })}
        />
      )}
    </>
  );
}
